import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@core/auth/auth.service';
import { Branch } from '@core/models/branch.model';
import { FeatureType } from '@core/models/features/feature.model';
import { WorkSpace } from '@core/models/workspace';
import { BranchService } from "@core/services/branch.service";
import { FeatureService } from '@core/services/feature.service';
import { OrganisationService } from '@core/services/organisation.service';
import { WorkSpaceService } from '@core/services/workspace.service';
import { Subject, combineLatest } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MenuComponent implements OnInit, OnDestroy {

  constructor(
    private router: Router,
    private authService: AuthService,
    public branchService: BranchService,
    private workspaceService:WorkSpaceService,
    protected fs:FeatureService,
    private organisationService:OrganisationService
  ) { }

  FeatureType = FeatureType;
  appLogo: string = '';
  appName: string = 'Smart Lions';
  catalog: string = '';
  adminBlock: boolean = false;
  qhse: boolean = false;
  communication: boolean = true;
  loading:boolean = true;
  gettingFeatures:boolean = true;
  userWorkSpace!:WorkSpace;
  orgWorkSpaces:WorkSpace[] = [];
  orgWorkSpacesLoaded:boolean = false;
  currentOrgWorkSpace:WorkSpace|null = null;
  attachmentModuleEnabled:boolean = false;


  get hasMoreMenuItems(): boolean {
    if(this.branchService.vanityMode){
      return false;
    }
    return this.fs.hasFeature(FeatureType.ENROLMENTS_ACCESS) ||
      this.fs.hasFeature(FeatureType.CONNECTIONS_ACCESS) ||
      (this.attachmentModuleEnabled && this.fs.hasFeature(FeatureType.ATTACHMENTS_ACCESS));
  }

  ngOnInit(): void {
    this.router.events.pipe(takeUntil(this.stopRequesting), filter(event => event instanceof NavigationEnd)).subscribe(event =>{
       this.hide();
    });

    this.fs.features$.pipe(takeUntil(this.stopRequesting)).subscribe((features) => {this.gettingFeatures = features === null})

    this.organisationService.currentOrganisation$.pipe(takeUntil(this.stopRequesting)).subscribe(org => {
      this.attachmentModuleEnabled = org?.attachmentModuleEnabled ?? false;
    });
    
    this.workspaceService.orgWorkSpaces$.pipe(switchMap(orgSpaces =>{

      this.orgWorkSpaces = orgSpaces ?? [];
      this.orgWorkSpacesLoaded = orgSpaces !== null;
      return combineLatest({
        userSpace:this.workspaceService.userWorkSpace$.pipe(filter((ws): ws is WorkSpace => ws !== null)),
        orgSpace: this.workspaceService.currentOrgWorkSpace$
      })
    })).pipe(takeUntil(this.stopRequesting)).subscribe(result =>{
      
        this.userWorkSpace = result.userSpace;
               
        if(result.orgSpace !== null){
          
          this.orgWorkSpaces.forEach(ows => {
            ows.active = ows.id == result.orgSpace!.id;
            ows.selected = ows.id == result.orgSpace!.id;
          });

          const f = this.orgWorkSpaces.find(x => x.id == result.orgSpace!.id);
          if(f){
            f.active = result.orgSpace!.active;
            this.currentOrgWorkSpace = f;
            if(!f.active){
              this.userWorkSpace.active = true;  
            }
          }
        }else if(this.orgWorkSpaces.length){
          this.currentOrgWorkSpace = this.orgWorkSpaces[0];
          if(!this.currentOrgWorkSpace.active){
            this.userWorkSpace.active = true;
          }
        }else{
          this.userWorkSpace.active = true;
        }
        
        this.loading = false; 
     })



     if(localStorage.getItem('admin_block')){
       this.adminBlock = (localStorage.getItem('admin_block') === "true");
     }

     if (this.authService.getToken().qhse) {
       this.qhse = true;
     }

      this.branchService.getBranch().pipe(takeUntil(this.stopRequesting),filter((branch): branch is Branch => branch !== null)).subscribe( (branch: Branch) => {

          this.appLogo = branch.id ? branch.logo: '';
          this.appName = branch.name;
         //  if(this.branchService.branch.flowsparks){
         //    this.flowsparks = true;
         //  }

        if(branch.catalogUrl){
         this.catalog = branch.catalogUrl;
        }

      });
  }


  show(): void {
    const htmlTag = document.querySelector('html');
    if (htmlTag) {
      htmlTag.classList.add('menu-locked');
    }
  }

  hide(): void {
    const htmlTag = document.querySelector('html');
    if (htmlTag) {
      htmlTag.classList.remove('menu-locked');
    }
  }

  logout(){
    localStorage.removeItem('redirectUrl_sl');
    this.branchService.clearReferral();
    this.authService.logout();
    this.workspaceService.clear();
    this.router.navigateByUrl('/login');
  }
  /** Both matrices read the catalogue; with neither type readable there is nothing to open. */
  get canReadQualifications():boolean {
    return this.fs.hasFeature(FeatureType.CERTIFICATIONS_ACCESS) || this.fs.hasFeature(FeatureType.QUALIFICATIONS_ACCESS);
  }

  /**
   * Comparing people against a function or a colleague is a qualification-module screen: it reads
   * levels, and ranks people by how far they are from them. Without the module there is nothing to
   * compare, so the way in is not offered at all.
   */
  get canCompare(): boolean {
    return this.fs.hasFeature(FeatureType.QUALIFICATIONS_ACCESS);
  }

  /** The catalogue page is there to manage it, so it is only offered to whoever may. */
  get canManageCatalogue():boolean {
    return this.fs.hasFeature(FeatureType.CERTIFICATIONS_EDIT) || this.fs.hasFeature(FeatureType.QUALIFICATIONS_EDIT);
  }

  /**
   * Selling a module only makes sense to whoever can buy it: the add-on cards live in the settings,
   * which only an admin opens. Anyone else would land on a page with nothing on it.
   */
  get canBuyModules():boolean {
    return this.fs.hasFeature(FeatureType.SETTINGS_ACCESS);
  }

  /**
   * Neither the certification nor the qualification module: the section is still listed, every
   * item with the premium mark, and its pages offer the modules instead of a matrix. With the
   * certification module it is in the menu already, so only the compare matrix is marked there.
   */
  get qualificationsUpsell():boolean {
    return !this.canReadQualifications && this.canBuyModules && this.fs.hasFeature(FeatureType.QUALIFICATIONS_SECTION_ACCESS);
  }

  /** The section is listed: readable, or to be sold. */
  get showQualifications():boolean {
    return this.canReadQualifications || this.qualificationsUpsell;
  }

  /** The compare matrix takes the qualification module; offered with the premium mark where it is missing. */
  get showCompare():boolean {
    return this.canCompare || (this.canBuyModules && this.fs.hasFeature(FeatureType.QUALIFICATIONS_SECTION_ACCESS));
  }

  /** The catalogue's page, where the upsell happens on its add button. */
  get showCatalogue():boolean {
    return this.canManageCatalogue || (this.qualificationsUpsell && this.fs.hasFeature(FeatureType.QUALIFICATIONS_SECTION_EDIT));
  }


  qualificationSubmenuOpen = false;
  moreSubmenuOpen = false;
  submenuPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom' },
  ];

  private qualificationSubmenuTimeout: any;
  private moreSubmenuTimeout: any;

  private get isMobile(): boolean {
    return window.innerWidth <= 1280;
  }

  openQualificationSubmenu() {
    if (this.isMobile) return;
    clearTimeout(this.qualificationSubmenuTimeout);
    this.qualificationSubmenuOpen = true;
  }

  closeQualificationSubmenu() {
    this.qualificationSubmenuTimeout = setTimeout(() => this.qualificationSubmenuOpen = false, 150);
  }

  openMoreSubmenu() {
    if (this.isMobile) return;
    clearTimeout(this.moreSubmenuTimeout);
    this.moreSubmenuOpen = true;
  }

  closeMoreSubmenu() {
    this.moreSubmenuTimeout = setTimeout(() => this.moreSubmenuOpen = false, 150);
  }

  stopRequesting = new Subject();
  ngOnDestroy() {
   this.stopRequesting.next(true);
 }
}
